import { FC, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Lock, Users } from 'lucide-react';
import { foldersApi, Folder, FolderVisibility } from '@/api/folders';
import { useGroups, useTenantMembers } from '@/hooks/useTenants';
import { useConfirm } from '@/components/common/dialogContext';

// 目录级权限弹窗（10-01 立项批 D，纯前端）：继承 / 私密 / 指定成员与分组 三态。
// 打开时 GET shares 回显，保存 PUT 全量替换；仅在团队空间由树节点菜单打开
// （个人空间后端 400，入口在 SidebarFolderTree 按 activeTenant 收口）。
// 注意：文件夹列表响应未透出 visibility 字段（后端 _folder_item 未带），
// 故树节点不显示锁图标，当前权限状态只在本弹窗打开后可见。
interface FolderPermDialogProps {
  folder: Folder;
  tenantId: string;
  onClose: () => void;
}

const VISIBILITY_OPTIONS: { value: FolderVisibility; label: string; desc: string }[] = [
  { value: 'inherit', label: '继承（默认）', desc: '跟随父夹；无受限父夹时全员可见' },
  { value: 'private', label: '私密', desc: '仅创建者与租户 owner/admin 可见' },
  { value: 'restricted', label: '指定成员与分组', desc: '仅创建者、管理员与下方授权对象可见' },
];

const FolderPermDialog: FC<FolderPermDialogProps> = ({ folder, tenantId, onClose }) => {
  const queryClient = useQueryClient();
  const askConfirm = useConfirm();

  // 回显数据：visibility + 授权清单（key 挂在 ['folders'] 前缀下，内容失效会一并带走）
  const { data: sharesData, isLoading } = useQuery({
    queryKey: ['folders', folder.id, 'shares'],
    queryFn: async () => (await foldersApi.getShares(folder.id)).data,
    staleTime: 30 * 1000,
  });
  const { members } = useTenantMembers(tenantId);
  const { groups } = useGroups(tenantId);

  const [visibility, setVisibility] = useState<FolderVisibility>('inherit');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // GET 返回后回显（只灌一次：用户编辑中不能被慢响应覆盖）
  useEffect(() => {
    if (!sharesData) return;
    setVisibility(sharesData.visibility);
    setSelectedUsers(new Set(
      sharesData.shares.filter((s) => s.grantee_type === 'user').map((s) => s.grantee_id)
    ));
    setSelectedGroups(new Set(
      sharesData.shares.filter((s) => s.grantee_type === 'group').map((s) => s.grantee_id)
    ));
  }, [sharesData]);

  const toggleSet = (setter: typeof setSelectedUsers, id: string) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setError(null);
    const shares = visibility === 'restricted'
      ? [
          ...[...selectedUsers].map((id) => ({ grantee_type: 'user' as const, grantee_id: id })),
          ...[...selectedGroups].map((id) => ({ grantee_type: 'group' as const, grantee_id: id })),
        ]
      : [];
    // 原授权行将被清空（降级 inherit/private，或 restricted 但清空勾选）先确认——
    // 血泪#108：确认框必须走 DialogHost 的 useConfirm，不能 window.confirm
    if ((sharesData?.shares.length || 0) > 0 && shares.length === 0) {
      const ok = await askConfirm('保存后本文件夹已有的授权成员/分组将全部被清空，确定继续吗？', '清空并保存');
      if (!ok) return;
    }
    setSaving(true);
    try {
      await foldersApi.setVisibility(folder.id, { visibility, shares });
      // 文件夹口径变更会影响整棵树的可见范围，['folders'] 前缀失效（含本弹窗的 shares 查询）
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      onClose();
      // DialogHost 把 window.alert 收编成右上角 toast（Electron 不支持原生 alert）
      window.alert('权限设置已保存');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={() => !saving && onClose()}>
      <div className="absolute inset-0 bg-black/80" />
      <div
        className="relative z-10 w-full max-w-[420px] mx-4 glass-card p-5 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold text-text-primary mb-1 flex items-center gap-1.5">
          <Lock className="w-4 h-4 text-info" />
          「{folder.name}」的权限
        </h3>
        <p className="text-[11px] text-text-secondary mb-3 leading-relaxed">
          目录级权限仅团队空间生效；子文件夹默认跟随父夹。
        </p>

        {isLoading ? (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="w-6 h-6 text-info animate-spin" />
          </div>
        ) : (
          <>
            <div className="space-y-1.5 mb-3">
              {VISIBILITY_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-center gap-2.5 p-2.5 rounded-[2px] border cursor-pointer transition-all ${
                    visibility === opt.value
                      ? 'border-info/40 bg-info/5'
                      : 'border-white/[0.08] hover:border-white/[0.15]'
                  }`}
                >
                  <input
                    type="radio"
                    name="folderVisibility"
                    value={opt.value}
                    checked={visibility === opt.value}
                    onChange={() => setVisibility(opt.value)}
                    className="accent-info"
                  />
                  <div>
                    <div className="text-xs font-medium text-text-primary">{opt.label}</div>
                    <div className="text-[11px] text-text-muted">{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            {visibility === 'restricted' && (
              <div className="mb-3 space-y-3">
                <div>
                  <div className="text-[11px] text-text-muted mb-1 flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    授权成员（{selectedUsers.size}）
                  </div>
                  {!members || members.length === 0 ? (
                    <p className="text-[11px] text-text-muted px-1">本租户暂无成员</p>
                  ) : (
                    <div className="max-h-36 overflow-y-auto space-y-1 pr-1">
                      {members.map((m) => (
                        <label key={m.user_id} className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(m.user_id)}
                            onChange={() => toggleSet(setSelectedUsers, m.user_id)}
                            className="accent-fusion-primary"
                          />
                          <span className="truncate">{m.name || m.email}</span>
                          {m.name && <span className="text-[10px] text-text-muted truncate">{m.email}</span>}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-[11px] text-text-muted mb-1">授权分组（{selectedGroups.size}）</div>
                  {!groups || groups.length === 0 ? (
                    <p className="text-[11px] text-text-muted px-1">本租户暂无分组</p>
                  ) : (
                    <div className="max-h-36 overflow-y-auto space-y-1 pr-1">
                      {groups.map((g) => (
                        <label key={g.id} className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedGroups.has(g.id)}
                            onChange={() => toggleSet(setSelectedGroups, g.id)}
                            className="accent-fusion-primary"
                          />
                          <span className="truncate">{g.name}</span>
                          <span className="text-[10px] text-text-muted">{g.member_count} 人</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={saving}
                className="flex-1 px-3 py-1.5 rounded-[2px] text-xs bg-bg-tertiary border border-border-color text-text-secondary hover:text-text-primary transition-colors disabled:opacity-60"
              >
                取消
              </button>
              <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary text-xs py-1.5 disabled:opacity-60">
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
            {error && <div className="mt-2 text-[11px] text-danger leading-snug">{error}</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default FolderPermDialog;
